/**
 * Shared attachment security policy.
 *
 * Storage accepts arbitrary user files, but delivery and file consumption are
 * gated by this policy. The built-in list is the union of the Gmail and
 * Outlook/Exchange blocked-extension lists and may only be expanded at runtime.
 */
const GMAIL_BLOCKED_EXTENSIONS = [
    'ade', 'adp', 'apk', 'appx', 'appxbundle', 'bat', 'cab', 'chm', 'cmd', 'com',
    'cpl', 'diagcab', 'diagcfg', 'diagpkg', 'dll', 'dmg', 'ex', 'ex_', 'exe',
    'hta', 'img', 'ins', 'iso', 'isp', 'jar', 'jnlp', 'js', 'jse', 'lib', 'lnk',
    'mde', 'mjs', 'msc', 'msi', 'msix', 'msixbundle', 'msp', 'mst', 'nsh', 'pif',
    'ps1', 'scr', 'sct', 'shb', 'sys', 'vb', 'vbe', 'vbs', 'vhd', 'vxd', 'wsc',
    'wsf', 'wsh', 'xll',
];
const OUTLOOK_BLOCKED_EXTENSIONS = [
    'ade', 'adp', 'apk', 'app', 'appx', 'appcontent-ms', 'application', 'appref-ms',
    'asp', 'aspx', 'asx', 'bas', 'bat', 'bgi', 'cab', 'cdxml', 'cer', 'chm', 'cmd',
    'cnt', 'com', 'cpl', 'crt', 'csh', 'der', 'diagcab', 'exe', 'fxp', 'gadget',
    'grp', 'hlp', 'hpj', 'hta', 'htc', 'img', 'inf', 'ins', 'iso', 'isp', 'its',
    'jar', 'jnlp', 'js', 'jse', 'ksh', 'library-ms', 'lnk', 'mad', 'maf', 'mag',
    'mam', 'maq', 'mar', 'mas', 'mat', 'mau', 'mav', 'maw', 'mcf', 'mda', 'mdb',
    'mde', 'mdt', 'mdw', 'mdz', 'mht', 'mhtml', 'msc', 'msh', 'msh1', 'msh1xml',
    'msh2', 'msh2xml', 'mshxml', 'msi', 'msp', 'mst', 'msu', 'ops', 'osd', 'pcd',
    'pif', 'pl', 'plg', 'prf', 'prg', 'printerexport', 'ps1', 'ps1xml', 'ps2',
    'ps2xml', 'psc1', 'psc2', 'psd1', 'psdm1', 'pssc', 'pst', 'py', 'pyc', 'pyo',
    'pyw', 'pyz', 'pyzw', 'reg', 'scf', 'scr', 'search-ms', 'sct', 'settingcontent-ms',
    'shb', 'shs', 'theme', 'tmp', 'udl', 'url', 'vb', 'vbe', 'vbp', 'vbs', 'vhd',
    'vhdx', 'vsmacros', 'vsw', 'webpnp', 'website', 'ws', 'wsb', 'wsc', 'wsf',
    'wsh', 'xbap', 'xll', 'xnk',
];
export const PROVIDER_BLOCKED_EXTENSIONS = [
    ...new Set([...GMAIL_BLOCKED_EXTENSIONS, ...OUTLOOK_BLOCKED_EXTENSIONS]),
];
export const PROVIDER_BLOCKED_MIME_TYPES = [
    'application/x-dosexec',
    'application/x-msdownload',
    'application/vnd.microsoft.portable-executable',
    'application/x-executable',
];
export const ARCHIVE_EXTENSIONS = [
    'zip', 'gz', 'gzip', 'tgz', 'tar', 'bz2', 'bzip2', '7z', 'rar', 'cab',
];
function normalizedExtension(filename) {
    const lastSegment = filename.replaceAll('\\', '/').split('/').pop() ?? filename;
    const dot = lastSegment.lastIndexOf('.');
    return dot > 0 ? lastSegment.slice(dot + 1).toLowerCase() : '';
}
export function isArchiveFilename(filename) {
    return ARCHIVE_EXTENSIONS.includes(normalizedExtension(filename));
}
export function getProviderBlockSources(filename, mimeType, config = {}) {
    const extension = normalizedExtension(filename);
    const blockedExtensions = new Set([
        ...PROVIDER_BLOCKED_EXTENSIONS,
        ...(config.additionalBlockedExtensions ?? []).map((value) => value.replace(/^\./, '').toLowerCase()),
    ]);
    const blockedMimeTypes = new Set([
        ...PROVIDER_BLOCKED_MIME_TYPES,
        ...(config.additionalBlockedMimeTypes ?? []).map((value) => value.toLowerCase()),
    ]);
    if (!blockedExtensions.has(extension) && !blockedMimeTypes.has(mimeType?.toLowerCase() ?? ''))
        return [];
    const builtInExtensionBlocked = PROVIDER_BLOCKED_EXTENSIONS.includes(extension);
    const additionalExtensionBlocked = (config.additionalBlockedExtensions ?? [])
        .map((value) => value.replace(/^\./, '').toLowerCase())
        .includes(extension);
    const gmailBlocked = GMAIL_BLOCKED_EXTENSIONS.includes(extension)
        || additionalExtensionBlocked
        || blockedMimeTypes.has(mimeType?.toLowerCase() ?? '');
    const outlookBlocked = OUTLOOK_BLOCKED_EXTENSIONS.includes(extension)
        || additionalExtensionBlocked
        || blockedMimeTypes.has(mimeType?.toLowerCase() ?? '');
    if (!builtInExtensionBlocked && !additionalExtensionBlocked && blockedMimeTypes.has(mimeType?.toLowerCase() ?? '')) {
        return ['gmail', 'outlook'];
    }
    return [
        ...(gmailBlocked ? ['gmail'] : []),
        ...(outlookBlocked ? ['outlook'] : []),
    ];
}
export function evaluateProviderAttachmentPolicy(filename, mimeType, config = {}) {
    const providerBlockSources = getProviderBlockSources(filename, mimeType, config);
    return providerBlockSources.length === 0
        ? { scanStatus: 'pending', policyVersion: config.version }
        : {
            policyVersion: config.version,
            scanStatus: 'rejected',
            scanResult: 'policy_rejected',
            providerBlockSources,
            providerBlockReason: 'File type is blocked by a connected mail provider',
        };
}
